import { useCallback, useMemo, useRef } from "react";

// Package Imports
import { ColDef, DefaultMenuItem, GetContextMenuItemsParams, MenuItemDef, SideBarDef } from "ag-grid-community";
import dayjs from "dayjs";

// Component Imports
import { AlgoStatusCell } from "../components";
import { DetailsToolPanel } from "../../ToolPanels";

// Types Imports
import { IAGAlgoDataRow, useAGTableProps } from "../AlgoTable.types";
import { AgGridReact } from "ag-grid-react";

// Utils Imports
import { getDefaultRpcsForConnection, isRpcDisabledForStatus } from "../../../utils";

export const useAGTable = ({
    dataMap,
    onInteract,
    interactionsData,
    onInteractToRpc,
}: useAGTableProps) => {
    const gridRef = useRef<AgGridReact<IAGAlgoDataRow> | null>(null);

    const detailsPanelSetRowRef = useRef<((row: IAGAlgoDataRow | null) => void) | null>(null);

    const rowData = useMemo(() => {
        const entries = Array.from(dataMap.entries());
        entries.sort(([a], [b]) => a.localeCompare(b));
        return entries.map(([, v]) => v);
    }, [dataMap]);

    const colDefs = useMemo<ColDef<IAGAlgoDataRow>[]>(
        () => [
            {
                headerName: "Strategy Instance ID",
                field: "strategy_instance_id",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Type",
                field: "type",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Connection ID",
                field: "connection_id",
                cellClass: "ag-left-aligned-cell",
                hide: true,
            },
            {
                headerName: "Portfolio",
                field: "portfolio",
                cellClass: "ag-left-aligned-cell",
                hide: true,
            },
            {
                headerName: "P&L",
                field: "pnl",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Status",
                field: "status",
                cellClass: "ag-center-aligned-cell",
                cellRenderer: AlgoStatusCell,
                cellRendererParams: (params: any) => ({
                    value: params.value,
                }),
            },
            {
                headerName: "CPU Usage",
                field: "cpu_usage",
                cellClass: "ag-center-aligned-cell",
                hide: true,
            },
            {
                headerName: "RAM Usage",
                field: "ram_usage",
                cellClass: "ag-center-aligned-cell",
                hide: true,
            },
            {
                headerName: "Strategy",
                field: "strategy",
                cellClass: "ag-center-aligned-cell",
            },
            {
                headerName: "Created At",
                field: "create_time",
                cellClass: "ag-center-aligned-cell",
                valueFormatter: (params: any) =>
                    dayjs(params.value).format("MM/DD/YYYY HH:mm:ss"),
            },
            {
                headerName: "Updated At",
                field: "update_time",
                cellClass: "ag-center-aligned-cell",
                valueFormatter: (params: any) =>
                    dayjs(params.value).format("MM/DD/YYYY HH:mm:ss"),
            },
            {
                headerName: "Accounts",
                field: "accounts",
                cellClass: "ag-left-aligned-cell",
                hide: true,
            },
        ],
        []
    );

    const defaultColDef: ColDef = {
        flex: 1,
        filter: true,
        sortable: true,
    };

    const getContextMenuItems = useCallback(
        (params: GetContextMenuItemsParams): (MenuItemDef | DefaultMenuItem)[] => {
            const row = params.node?.data as IAGAlgoDataRow | undefined;

            const customItems: MenuItemDef[] = [
                {
                    name: "Interact",
                    subMenu: [
                        {
                            name: "Details",
                            action: () => row && gridContext.openFullDetails(row),
                            cssClasses: ["pulse-color--background-50"],
                        },
                    ],
                },
            ];

            if (row && interactionsData && onInteractToRpc) {
                const defaultRpcs = getDefaultRpcsForConnection(
                    interactionsData,
                    row.connection_id
                );
                defaultRpcs.forEach(({ slot, rpcName, displayName }) => {
                    const disabled = isRpcDisabledForStatus(slot, row.status);
                    customItems[0].subMenu?.push({
                        name: displayName,
                        action: () => onInteractToRpc(row, rpcName),
                        disabled,
                        cssClasses: ["pulse-color--background-50"],
                    });
                });
            }

            return [
                ...customItems,
                "separator",
                ...((params.defaultItems || []) as unknown as MenuItemDef[]),
            ];
        },
        [onInteract, interactionsData, onInteractToRpc]
    );

    const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => {
        return {
            toolPanels: [
                "columns",
                "filters",
                {
                    id: "details",
                    labelDefault: "Details",
                    labelKey: "details",
                    iconKey: "custom-collection-icon",
                    toolPanel: DetailsToolPanel,
                    toolPanelParams: {
                        registerSetRow: (setRowFn: (row: IAGAlgoDataRow | null) => void) => {
                            detailsPanelSetRowRef.current = setRowFn;
                        },
                    },
                },
            ],
            // hiddenByDefault: false,
        };
    }, []);

    const gridContext = useMemo(
        () => ({
            openFullDetails: (row: IAGAlgoDataRow) => {
                onInteract(row);
            },
        }),
        [onInteract]
    );

    const handleCellDoubleClicked = useCallback(
        (params: any) => {
            const row = params.data as IAGAlgoDataRow | undefined;
            if (!row) return;

            if (gridRef.current?.api) {
                const api = gridRef.current.api;
                api.setSideBarVisible(true);
                api.openToolPanel("details");

                // setTimeout as safety net for first-time mount
                setTimeout(() => {
                    if (detailsPanelSetRowRef.current) {
                        detailsPanelSetRowRef.current(row);
                    }
                }, 50);
            }
        },
        []
    );

    return {
        gridRef,
        rowData,
        colDefs,
        defaultColDef,
        sideBar,
        gridContext,
        getContextMenuItems,
        handleCellDoubleClicked,
    };
}
